// Plantillas de arranque del MODO CÓDIGO del builder. Dos sabores:
//  - 'full'  : el código fuente REAL del builder (top-N, leyenda, grid, colores…),
//              para editar cualquier interno — "exactamente como lo hicimos a mano".
//  - 'base'  : un wrapper corto que llama al builder + deep-mergea overrides,
//              para arrancar rápido sin meterse en los internos.
// La función recibe el payload COMPLETO en `data` (puede combinar fuentes) y
// `helpers` con los mismos builders/utilidades de la app.
// Los tokens {{SOURCE}} / {{METRIC}} se sustituyen con la fuente del widget.

const DEFAULT_SOURCE = {
  multiLine: 'avgPriceByChain',
  line: 'timeseries',
  heatmap: 'matrix',
  gauge: '',
};

// ───────────────────────────── BASE (wrapper) ──────────────────────────────
const BASE = {
  multiLine: `(data, theme, helpers) => {
  const { buildMultiLineChart, deepMerge } = helpers;
  const slice = data["{{SOURCE}}"] || { dates: [], series: [] };
  const base = buildMultiLineChart(slice, theme, { format: "money", maxSeries: 12 });
  // Sobrescribe lo que quieras encima (los arrays se reemplazan completos):
  return deepMerge(base, {
    legend: { type: "scroll" }
  });
}`,
  line: `(data, theme, helpers) => {
  const { buildLineChartWithMarkLine, deepMerge } = helpers;
  const slice = data["{{SOURCE}}"] || [];
  const base = buildLineChartWithMarkLine(slice, theme, { x: "date", y: "priceIndex", format: "pct", targetValue: -10 });
  return deepMerge(base, {});
}`,
  heatmap: `(data, theme, helpers) => {
  const { buildHeatmapMatrix, deepMerge } = helpers;
  const slice = data["{{SOURCE}}"] || {};
  const base = buildHeatmapMatrix(slice, theme, { format: "pct" });
  return deepMerge(base, {});
}`,
  gauge: `(data, theme, helpers) => {
  const { buildKPIGauge, deepMerge } = helpers;
  const current = (data.kpis || {})["{{METRIC}}"];
  const base = buildKPIGauge({ current, format: "pct", min: -50, max: 50 }, theme, { label: "Price Index" });
  return deepMerge(base, {});
}`,
};

// ───────────────────────────── FULL (código real) ──────────────────────────
const FULL = {
  multiLine: `(data, theme, helpers) => {
  const { formatValue, axisFormatter, generateThemeColors } = helpers;

  // ===== Config editable =====
  const SOURCE = "{{SOURCE}}";
  const format = "money";
  const maxSeries = 12;        // top-N cadenas (las demás se ocultan)
  const smoothness = 0.4;
  const showArea = false;
  const colors = generateThemeColors(theme);

  // ===== Datos =====
  const slice = data[SOURCE] || { dates: [], series: [] };
  const dates = slice.dates || [];
  const all = slice.series || [];
  const series = maxSeries > 0 ? all.slice(0, maxSeries) : all;
  const hidden = Math.max(0, all.length - series.length);

  // ===== Series =====
  const seriesOptions = series.map((s, i) => ({
    name: s.name, type: "line", data: s.data, smooth: smoothness, showSymbol: false,
    lineStyle: { color: colors[i % colors.length], width: 1.6 },
    itemStyle: { color: colors[i % colors.length] },
    emphasis: { focus: "series" },
    ...(showArea && { areaStyle: { color: colors[i % colors.length] + "12" } }),
  }));

  // ===== Option =====
  return {
    backgroundColor: theme.pageBg || "transparent",
    tooltip: {
      trigger: "axis", order: "valueDesc",
      formatter: (params) => {
        if (!params || !params.length) return "";
        let html = "<strong>" + params[0].name + "</strong><br/>";
        params.forEach((p) => {
          const v = p.value == null ? "Sin datos" : formatValue(p.value, format);
          html += '<span style="color:' + p.color + '">● ' + p.seriesName + ": <strong>" + v + "</strong></span><br/>";
        });
        return html;
      },
    },
    legend: { type: "scroll", top: 6, textStyle: { color: theme.headerBg, fontSize: 11 } },
    grid: { left: 8, right: 18, top: 44, bottom: 24, containLabel: true },
    xAxis: { type: "category", data: dates, boundaryGap: false, axisLabel: { fontSize: 11, color: theme.headerBg } },
    yAxis: { type: "value", axisLabel: { formatter: axisFormatter(format), color: theme.headerBg, fontSize: 11 }, splitLine: { lineStyle: { color: theme.pageBg } } },
    series: seriesOptions,
    graphic: hidden > 0 ? [{ type: "text", right: 14, bottom: 4, z: 10, style: { text: "+" + hidden + " cadenas ocultas", fill: "#94a3b8", fontSize: 11 } }] : undefined,
  };
}`,
  line: `(data, theme, helpers) => {
  const { formatValue, axisFormatter } = helpers;

  const SOURCE = "{{SOURCE}}";
  const x = "date", y = "priceIndex", format = "pct", targetValue = -10;

  const rows = Array.isArray(data[SOURCE]) ? data[SOURCE] : [];
  const dates = rows.map((d) => d[x]);
  const values = rows.map((d) => d[y]);

  return {
    backgroundColor: theme.pageBg || "transparent",
    tooltip: { trigger: "axis", formatter: (p) => {
      if (!p || !p.length) return "";
      const v = p[0].value == null ? "—" : formatValue(p[0].value, format);
      return "<strong>" + p[0].name + "</strong><br/>" + p[0].seriesName + ": <strong>" + v + "</strong>";
    } },
    grid: { left: 8, right: 24, top: 20, bottom: 24, containLabel: true },
    xAxis: { type: "category", data: dates, axisLabel: { fontSize: 11, color: theme.headerBg } },
    yAxis: { type: "value", axisLabel: { formatter: axisFormatter(format), color: theme.headerBg, fontSize: 11 }, splitLine: { lineStyle: { color: theme.pageBg } } },
    series: [{
      name: "Price Index", type: "line", data: values, smooth: 0.4, showSymbol: false,
      lineStyle: { color: theme.primary, width: 2 },
      areaStyle: { color: (theme.primary || "#0E5C84") + "20" },
      markLine: { silent: true, lineStyle: { type: "dashed" }, data: [
        { yAxis: 0, lineStyle: { color: "#999" }, label: { formatter: formatValue(0, format) } },
        { yAxis: targetValue, lineStyle: { color: theme.negative || "#d73027", width: 2 }, label: { formatter: "Target: " + formatValue(targetValue, format) } },
      ] },
    }],
  };
}`,
  heatmap: `(data, theme, helpers) => {
  const SOURCE = "{{SOURCE}}";
  const m = data[SOURCE] || { departments: [], retailers: [], cells: {} };
  const departments = m.departments || [], retailers = m.retailers || [], cells = m.cells || {};
  const min = -50, max = 50, maxLabelCells = 120;

  const pts = [];
  departments.forEach((dep, di) => retailers.forEach((ret, ri) => {
    const v = cells[dep] && cells[dep][ret];
    if (v != null) pts.push([ri, di, v]);
  }));
  const showLabel = departments.length * retailers.length <= maxLabelCells;
  const trunc = (s) => (s && s.length > 18 ? s.slice(0, 17) + "…" : s);

  return {
    backgroundColor: theme.pageBg || "transparent",
    tooltip: { position: "top", formatter: (p) => {
      const a = p.value;
      return "<strong>" + departments[a[1]] + " × " + retailers[a[0]] + "</strong><br/>Index: <strong>" + Number(a[2]).toFixed(2) + "%</strong>";
    } },
    grid: { left: 8, right: 66, top: 12, bottom: 8, containLabel: true },
    xAxis: { type: "category", data: retailers, axisLabel: { fontSize: 10, color: theme.headerBg, rotate: 60, interval: 0, formatter: trunc }, splitArea: { show: true } },
    yAxis: { type: "category", data: departments, axisLabel: { fontSize: 11, color: theme.headerBg }, splitArea: { show: true } },
    visualMap: { type: "continuous", min, max, calculable: true, orient: "vertical", right: 8, top: "middle", itemHeight: 180, textStyle: { color: theme.headerBg },
      inRange: { color: [theme.negative || "#2BB6A3", "#bfe6df", "#f1f5f5", "#f6d6d4", theme.positive || "#E8615E"] }, dimension: 2 },
    series: [{ name: "Price Index", type: "heatmap", data: pts, itemStyle: { borderColor: "#fff", borderWidth: 1 },
      label: showLabel ? { show: true, formatter: (p) => Number(p.value[2]).toFixed(1) + "%", fontSize: 9, color: theme.headerBg } : { show: false } }],
  };
}`,
  gauge: `(data, theme, helpers) => {
  const metric = "{{METRIC}}";
  const current = (data.kpis || {})[metric] || 0;
  const min = -50, max = 50, title = "Price Index";

  return {
    backgroundColor: theme.pageBg || "transparent",
    title: { text: title, left: "center", top: 10, textStyle: { color: theme.headerBg, fontSize: 14, fontWeight: "bold" } },
    series: [{
      type: "gauge", center: ["50%", "60%"], radius: "70%", min, max,
      data: [{ value: current }],
      axisLine: { lineStyle: { color: [[0.3, theme.negative], [0.7, theme.accent], [1, theme.positive]], width: 8 } },
      pointer: { itemStyle: { color: theme.primary } },
      detail: { formatter: "{value}%", offsetCenter: [0, "30%"], textStyle: { color: theme.primary, fontSize: 16, fontWeight: "bold" } },
      progress: { itemStyle: { color: theme.primary }, width: 6 },
    }],
  };
}`,
};

export const TEMPLATE_KINDS = [
  { id: 'multiLine', label: 'Multilínea' },
  { id: 'line', label: 'Línea' },
  { id: 'heatmap', label: 'Heatmap' },
  { id: 'gauge', label: 'Gauge' },
];

export const TEMPLATE_FLAVORS = [
  { id: 'full', label: 'Completa (código real)' },
  { id: 'base', label: 'Base (wrapper)' },
];

/**
 * Devuelve el string de código de la plantilla, con la fuente/métrica del widget
 * ya sustituida.
 */
export function getTemplate(kind = 'multiLine', flavor = 'full', { source, metric } = {}) {
  const bank = flavor === 'base' ? BASE : FULL;
  const raw = bank[kind] || bank.multiLine;
  return raw
    .split('{{SOURCE}}').join(source || DEFAULT_SOURCE[kind] || '')
    .split('{{METRIC}}').join(metric || 'priceIndexGeneral');
}

export default getTemplate;
